// Handling Analysis tab (epic #292; Slice 1 = #293 brake balance & lockup).
// Per-RUN tool inside the per-week workspace, so the run is the free variable —
// an inline run selector (default: latest run for this week) drives the fetch.
// DESCRIPTIVE ONLY: cards state what the car did, never "move the bar this way"
// (the Pit-Adjustment engine once shipped reversed — measurement, not advice).
// Absolute slip% is a HELD number (its scale isn't in-sim-validated yet), shown
// muted behind a caption. NASCAR-trio only; other cars show an out-of-domain note.
import { useEffect, useMemo, useState } from "react";
import type { CSSProperties } from "react";
import { useParams } from "react-router-dom";
import { CarBadge } from "../components/CarBadge";
import { CarSwitcherBar, useWeekCars } from "../components/CarSwitcher";
import { useRunStore } from "../state/runStore";
import { api, ApiError } from "../utils/api";
import { carCatalogLabel } from "../utils/carCatalog";
import { formatSessionType } from "../utils/formatters";

interface WheelRow { wheel: string; medianSlipPct: number; maxSlipPct: number; lockedPct: number }
interface PhaseRow { n: number; frontSlipPct: number; rearSlipPct: number }
interface BrakeResult {
  nBraking: number;
  balance: "front locks first" | "rear locks first" | "balanced";
  frontRearDiffPct: number;
  lockupEvents: Record<string, number>;
  absActivePct: number | null;
  staticFrontBiasPct: number | null;
  dynBiasMeanPct: number | null;
  dynBiasRangePct: number | null;
  perPhase: Record<string, PhaseRow> | null;
  held: { frontSlipPct: number; rearSlipPct: number; wheels: WheelRow[] };
  lowSample: boolean;
}
interface ModeStat { mode: string; meanMm: number; minMm: number; maxMm: number; rangeMm: number }
interface PlatformResult {
  modes: ModeStat[];
  rollPerG: number | null;
  pitchPerG: number | null;
  perPhase: Record<string, Record<string, number>> | null;
  nSamples: number;
  lowSample: boolean;
}
interface StaggerResult {
  nStraight: number;
  nCorner: number;
  rearSide: "right" | "left" | "even";
  frontSide: "right" | "left" | "even";
  pathEffect: "grows" | "shrinks" | "flat" | "unknown";
  lowSample: boolean;
  held: {
    rearStaggerMs: number; rearStaggerPct: number | null;
    frontStaggerMs: number; frontStaggerPct: number | null;
    rearCornerSideDiffMs: number | null; rearPathEffectMs: number | null;
    meanSpeedMs: number | null;
  };
}
interface BalanceResult {
  nCorner: number;
  understeerGradDegG: number | null;
  perPhaseGradient: Record<string, number | null> | null;
  steeringRatio: number | null;
  yawAvailable: boolean;
  lowSample: boolean;
  held: { yawRatioMedian: number | null; pushPct: number | null; pushPeakSteerDeg: number | null };
}
interface Handling {
  inDomain: boolean;
  carId: string | null;
  carName: string | null;
  reason?: string;
  unavailable?: string;
  brake?: BrakeResult | null;
  platform?: PlatformResult | null;
  stagger?: StaggerResult | null;
  balance?: BalanceResult | null;
}

// #298 corpus ranking — one row per run at this week, analyzed or not.
interface RankingRow {
  runId: string;
  setupName: string | null;
  driverName: string | null;
  sessionDate: string | null;
  eventType: string | null;
  carId: string | null;
  carName: string | null;
  totalLaps: number | null;
  greenLaps: number | null;
  analyzed: boolean;
  understeerGradDegG: number | null;
  rollPerG: number | null;
  pitchPerG: number | null;
  brakeFrontRearDiffPct: number | null;
  brakeBalance: string | null;
  staggerRearSide: "left" | "right" | "even" | null;
  staggerPathEffect: "grows" | "shrinks" | "flat" | "unknown" | null;
  balanceNCorner: number | null;
  brakeNBraking: number | null;
  platformNSamples: number | null;
  lowSample: boolean | null;
}
interface RankingResponse {
  trackWeekId: string;
  carId: string | null;
  rows: RankingRow[];
  analyzedCount: number;
  total: number;
  pendingRecompute: number;
}

const PATH_EFFECT_COPY: Record<string, string> = {
  grows: "Stagger grows through corners (path effect)",
  shrinks: "Stagger drops through corners",
  flat: "Stagger holds steady through corners",
  unknown: "",
};

const MODE_LABEL: Record<string, string> = { heave: "Heave", pitch: "Pitch", roll: "Roll", warp: "Warp" };
const MODE_HINT: Record<string, string> = {
  heave: "average ride height",
  pitch: "front − rear (− = nose-down rake)",
  roll: "left − right (+ = leans right)",
  warp: "diagonal twist (dynamic crossweight)",
};

const PHASE_LABEL: Record<string, string> = { entry: "Entry (braking)", center: "Center", exit: "Exit (throttle)" };
const MIN_PHASE_SAMPLES = 10;  // below this, a phase verdict is a noise fluke — don't assert it

export default function HandlingPanel() {
  const { teamId, trackWeekId: routeWeekId } = useParams<{ teamId?: string; trackWeekId?: string }>();
  const { runs, fetchRuns, selectedTrackWeekId } = useRunStore();
  const weekId = routeWeekId || selectedTrackWeekId || undefined;

  const [view, setView] = useState<"run" | "compare">("run");
  const [runId, setRunId] = useState<string | null>(null);
  const [data, setData] = useState<Handling | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadNonce, setReloadNonce] = useState(0);

  // Runs for this week (reuse the store; no new endpoint). Default to the latest.
  useEffect(() => {
    if (teamId && weekId) void fetchRuns(teamId, weekId);
  }, [teamId, weekId, fetchRuns]);

  const weekRuns = useMemo(
    () => runs.filter((r) => r.trackWeekId === weekId),
    [runs, weekId],
  );

  useEffect(() => {
    // Default to the latest run ONLY when nothing is selected. Do NOT clobber a
    // runId that isn't in weekRuns — a Compare-view click can open a run beyond
    // the /api/runs 50-cap, and that click must win.
    if (weekRuns.length && !runId) {
      setRunId(weekRuns[0].id); // runs come newest-first
    }
  }, [weekRuns, runId]);

  // Switching weeks resets the selection so the new week defaults to its latest.
  useEffect(() => {
    setRunId(null);
  }, [weekId]);

  useEffect(() => {
    if (!runId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api<Handling>(`/api/strategy/handling?run_id=${runId}`)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((err) => {
        if (cancelled) return;
        // 503 = analyzer semaphore busy (re-read guard) — "try again", not permanent.
        setError(
          err instanceof ApiError && err.status === 503
            ? "The analyzer is busy right now — try again in a few seconds."
            : "Couldn't analyze this run."
        );
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [runId, reloadNonce]);

  return (
    <div className="page fade-in">
      <header className="editorial-header">
        <div className="editorial-meta">
          <span className="eyebrow">Telemetry</span>
        </div>
        <h1 className="editorial-title">Handling Analysis</h1>
      </header>

      {/* View switch: one run in depth vs the week-wide comparison table (#298). */}
      <div style={st.viewRow}>
        <div style={st.segment} role="radiogroup" aria-label="View">
          {(["run", "compare"] as const).map((v) => {
            const on = view === v;
            return (
              <button
                key={v}
                type="button"
                role="radio"
                aria-checked={on}
                style={{ ...st.seg, ...(on ? st.segOn : null) }}
                onClick={() => setView(v)}
              >
                {v === "run" ? "This run" : "Compare runs"}
              </button>
            );
          })}
        </div>
      </div>

      {view === "compare" && (
        <RankingView
          weekId={weekId}
          weekRunCount={weekRuns.length}
          onOpenRun={(id) => {
            setRunId(id);
            setView("run");
          }}
        />
      )}

      {view === "run" && (<>
      {/* Run selector */}
      <div style={st.selectorRow}>
        <label style={st.selectorLabel} htmlFor="handling-run">Run</label>
        <select
          id="handling-run"
          value={runId || ""}
          onChange={(e) => setRunId(e.target.value || null)}
          style={st.select}
          disabled={!weekRuns.length}
        >
          {!weekRuns.length && <option value="">No runs this week</option>}
          {weekRuns.map((r) => (
            <option key={r.id} value={r.id}>
              {(r.displayName || "Driver")} · {r.setupName || "setup"}
              {r.totalLaps != null ? ` · ${r.totalLaps} laps` : ""}
              {r.sessionDate ? ` · ${new Date(r.sessionDate).toLocaleDateString()}` : ""}
            </option>
          ))}
        </select>
      </div>

      {loading && <div className="empty-state" style={{ minHeight: "10rem" }}><div className="empty-state-text">Analyzing run…</div></div>}
      {error && !loading && (
        <div className="empty-state" style={{ minHeight: "10rem" }}>
          <div className="empty-state-text">{error}</div>
          <button
            type="button"
            className="btn btn-secondary"
            style={{ marginTop: "0.75rem" }}
            onClick={() => setReloadNonce((n) => n + 1)}
          >
            Retry
          </button>
        </div>
      )}

      {!loading && !error && data && !data.inDomain && (
        <div className="card" style={st.card}>
          <div style={st.cardTitle}>Not calibrated for this car</div>
          <div style={st.muted}>{data.reason || "Handling analysis isn't calibrated for this car."}{data.carName ? ` (${data.carName})` : ""}</div>
        </div>
      )}

      {!loading && !error && data && data.inDomain && !data.brake && !data.platform && !data.stagger && !data.balance && (
        <div className="card" style={st.card}>
          <div style={st.cardTitle}>Handling</div>
          <div style={st.muted}>{data.unavailable || "This run's telemetry doesn't have the channels needed for handling analysis."}</div>
        </div>
      )}

      {!loading && !error && data?.brake && <BrakeCard b={data.brake} carName={data.carName} />}
      {!loading && !error && data?.platform && <PlatformCard p={data.platform} carName={data.carName} />}
      {!loading && !error && data?.stagger && <StaggerCard s={data.stagger} carName={data.carName} />}
      {!loading && !error && data?.balance && <BalanceCard b={data.balance} carName={data.carName} />}
      </>)}
    </div>
  );
}

// ---- Compare runs (#298). Descriptive-only: the sort order + plain metric
// captions carry the comparison — no rank numbers, no medals, no value coloring
// (the active sort column is emphasized by ink weight, not verdict color).
type RankCol = "session" | "laps" | "understeer" | "roll" | "pitch" | "brake";
type RankDir = "asc" | "desc";

const fin = (v: number | null) => (v != null && Number.isFinite(v) ? v : null);

function rankSortValue(r: RankingRow, col: RankCol): number | null {
  switch (col) {
    case "session": {
      if (!r.sessionDate) return null;
      const t = Date.parse(r.sessionDate);
      return Number.isFinite(t) ? t : null;
    }
    case "laps":
      return r.greenLaps ?? r.totalLaps;
    case "understeer":
      return fin(r.understeerGradDegG);
    case "roll":
      return fin(r.rollPerG);
    case "pitch":
      return fin(r.pitchPerG);
    case "brake":
      return fin(r.brakeFrontRearDiffPct);
  }
}

/** "R stagger, grows" — a direction descriptor, never a ranked column. */
function staggerChipLabel(r: RankingRow): string | null {
  if (!r.staggerRearSide) return null;
  if (r.staggerRearSide === "even") return "Even";
  const side = r.staggerRearSide === "right" ? "R stagger" : "L stagger";
  const path =
    r.staggerPathEffect === "grows" ? ", grows"
    : r.staggerPathEffect === "shrinks" ? ", shrinks"
    : r.staggerPathEffect === "flat" ? ", flat"
    : "";
  return side + path;
}

function RankingView({
  weekId,
  weekRunCount,
  onOpenRun,
}: {
  weekId: string | undefined;
  weekRunCount: number;
  onOpenRun: (runId: string) => void;
}) {
  const { cars, multi, selectedCarId, setCar } = useWeekCars(weekId);
  // Single-car weeks send no carId at all — the backend sees the whole week.
  const carParam = multi ? selectedCarId : undefined;

  const [data, setData] = useState<RankingResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadNonce, setReloadNonce] = useState(0);
  const [sortCol, setSortCol] = useState<RankCol>("session");
  const [sortDir, setSortDir] = useState<RankDir>("desc");

  useEffect(() => {
    if (!weekId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    const qs =
      `trackWeekId=${encodeURIComponent(weekId)}` +
      (carParam ? `&carId=${encodeURIComponent(carParam)}` : "");
    api<RankingResponse>(`/api/strategy/handling/ranking?${qs}`)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((err) => {
        if (cancelled) return;
        setError(
          err instanceof ApiError && err.status === 403
            ? "You are not a member of this team."
            : err instanceof ApiError && err.status === 404
              ? "This week wasn't found."
              : "Couldn't load the comparison."
        );
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [weekId, carParam, reloadNonce]);

  const onSort = (col: RankCol) => {
    if (col === sortCol) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortCol(col);
      // Fresh column: newest sessions / longest runs first; metrics low-first.
      setSortDir(col === "session" || col === "laps" ? "desc" : "asc");
    }
  };

  const rows = data?.rows ?? null;
  const sorted = useMemo(() => {
    if (!rows) return [];
    const dir = sortDir === "asc" ? 1 : -1;
    const tiebreak = (a: RankingRow, b: RankingRow) =>
      a.runId < b.runId ? -1 : a.runId > b.runId ? 1 : 0;
    return [...rows].sort((a, b) => {
      const va = rankSortValue(a, sortCol);
      const vb = rankSortValue(b, sortCol);
      // "—" rows (unanalyzed, or too few samples) sink to the bottom either way.
      if (va == null && vb == null) {
        const ta = a.sessionDate ? Date.parse(a.sessionDate) : 0;
        const tb = b.sessionDate ? Date.parse(b.sessionDate) : 0;
        return tb - ta || tiebreak(a, b);
      }
      if (va == null) return 1;
      if (vb == null) return -1;
      if (va !== vb) return (va - vb) * dir;
      return tiebreak(a, b);
    });
  }, [rows, sortCol, sortDir]);

  const fmtSession = (r: RankingRow) => {
    const date = r.sessionDate
      ? new Date(r.sessionDate).toLocaleDateString("en-US", { month: "short", day: "numeric" })
      : null;
    const type = r.eventType ? formatSessionType(r.eventType) : null;
    return [date, type].filter(Boolean).join(" · ") || "—";
  };

  const lapsCell = (r: RankingRow) => {
    if (r.greenLaps != null) {
      return (
        <>
          <span style={{ color: "var(--flag-green)" }}>{r.greenLaps}g</span>
          <span style={{ color: "var(--text-secondary)" }}>
            {r.totalLaps != null ? ` / ${r.totalLaps}` : ""}
          </span>
        </>
      );
    }
    return r.totalLaps != null ? String(r.totalLaps) : "—";
  };

  return (
    <>
      <CarSwitcherBar cars={cars} selectedCarId={selectedCarId} setCar={setCar} />

      {!weekId && (
        <div className="empty-state" style={{ minHeight: "10rem" }}>
          <div className="empty-state-text">Select a track week to compare runs.</div>
        </div>
      )}

      {weekId && loading && (
        <div className="empty-state" style={{ minHeight: "10rem" }}>
          <div className="empty-state-text">Loading runs…</div>
        </div>
      )}

      {error && !loading && (
        <div className="empty-state" style={{ minHeight: "10rem" }}>
          <div className="empty-state-text">{error}</div>
          <button
            type="button"
            className="btn btn-secondary"
            style={{ marginTop: "0.75rem" }}
            onClick={() => setReloadNonce((n) => n + 1)}
          >
            Retry
          </button>
        </div>
      )}

      {!loading && !error && data && data.rows.length === 0 && (
        <div className="empty-state" style={{ minHeight: "10rem" }}>
          {weekRunCount > 0 ? (
            // The corpus is NASCAR-trio only. Runs exist, but none are in-domain —
            // don't tell the user to "upload a run" (they have; wrong cars).
            <>
              <div className="empty-state-title">Not calibrated for these cars</div>
              <div className="empty-state-text">
                {carParam
                  ? "Handling comparison covers the Cup Next Gen, Xfinity, and Trucks. This car isn't one of them."
                  : "Handling comparison covers the Cup Next Gen, Xfinity, and Trucks. This week's runs are other cars."}
              </div>
            </>
          ) : (
            <>
              <div className="empty-state-title">No runs yet</div>
              <div className="empty-state-text">
                Upload a run to this week to compare handling across setups.
              </div>
            </>
          )}
        </div>
      )}

      {!loading && !error && data && sorted.length > 0 && (
        <div className="card" style={st.tableCard}>
          {data.total > 0 && (
            <div style={st.countLine}>
              <span style={{ color: "var(--text-strong)", fontWeight: 600 }}>
                {data.analyzedCount} of {data.total} runs analyzed
              </span>
              {data.pendingRecompute > 0 && <span> · {data.pendingRecompute} awaiting analysis</span>}
            </div>
          )}
          {data.total <= 1 && (
            <div style={st.countLine}>Comparisons need at least two runs.</div>
          )}
          <table className="table">
            <thead>
              <tr>
                <RankSortTh label="Session" col="session" sortCol={sortCol} sortDir={sortDir} onSort={onSort} />
                <th>Driver</th>
                {multi && <th>Car</th>}
                <RankSortTh label="Laps" col="laps" sortCol={sortCol} sortDir={sortDir} onSort={onSort} numeric />
                <RankSortTh
                  label="Understeer (°/g)" col="understeer" sortCol={sortCol} sortDir={sortDir} onSort={onSort} numeric
                  title="Steering degrees per g; higher = more front push"
                />
                <RankSortTh
                  label="Roll (mm/g)" col="roll" sortCol={sortCol} sortDir={sortDir} onSort={onSort} numeric
                  title="Body roll per g; lower = flatter in roll"
                />
                <RankSortTh
                  label="Pitch (mm/g)" col="pitch" sortCol={sortCol} sortDir={sortDir} onSort={onSort} numeric
                  title="Body pitch per g; lower = flatter in pitch"
                />
                <RankSortTh
                  label="Brake bal. (F−R)" col="brake" sortCol={sortCol} sortDir={sortDir} onSort={onSort} numeric
                  title="Front − rear lockup; 0 = even, + = front-biased. Compare runs, not as an absolute %"
                />
                <th title="Wheel-speed side difference; a direction, not a ranking">Stagger</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((r) => {
                const pending = !r.analyzed;
                const sub = [r.setupName, r.lowSample ? "few samples" : null]
                  .filter(Boolean)
                  .join(" · ");
                const chip = pending ? null : staggerChipLabel(r);
                const open = () => onOpenRun(r.runId);
                const sessionLabel = [fmtSession(r), r.driverName].filter(Boolean).join(" · ");
                return (
                  <tr
                    key={r.runId}
                    style={st.rowClick}
                    role="link"
                    tabIndex={0}
                    aria-label={`Open run — ${sessionLabel}`}
                    title="Open this run"
                    onClick={open}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        open();
                      }
                    }}
                  >
                    <td>
                      {fmtSession(r)}
                      {sub ? <div style={st.subLine}>{sub}</div> : null}
                    </td>
                    <td>{r.driverName || "—"}</td>
                    {multi && (
                      <td>
                        {r.carId ? (
                          <span style={st.carCell}>
                            <CarBadge slug={r.carId} size={18} />
                            {carCatalogLabel(r.carId)}
                          </span>
                        ) : (
                          r.carName || "—"
                        )}
                      </td>
                    )}
                    <td style={st.num}>{lapsCell(r)}</td>
                    <MetricTd
                      value={fin(r.understeerGradDegG)}
                      fmt={(v) => v.toFixed(1)}
                      active={sortCol === "understeer"}
                      reason={pending ? "Not analyzed yet" : "Too few corners to measure"}
                      title={r.balanceNCorner != null ? `${r.balanceNCorner} corner samples` : undefined}
                    />
                    <MetricTd
                      value={fin(r.rollPerG)}
                      fmt={(v) => v.toFixed(1)}
                      active={sortCol === "roll"}
                      reason={pending ? "Not analyzed yet" : "Too few samples to measure"}
                      title={r.platformNSamples != null ? `${r.platformNSamples} platform samples` : undefined}
                    />
                    <MetricTd
                      value={fin(r.pitchPerG)}
                      fmt={(v) => v.toFixed(1)}
                      active={sortCol === "pitch"}
                      reason={pending ? "Not analyzed yet" : "Too few samples to measure"}
                      title={r.platformNSamples != null ? `${r.platformNSamples} platform samples` : undefined}
                    />
                    <MetricTd
                      value={fin(r.brakeFrontRearDiffPct)}
                      fmt={(v) => `${v > 0 ? "+" : ""}${v.toFixed(1)}`}
                      active={sortCol === "brake"}
                      reason={pending ? "Not analyzed yet" : "Too few braking samples"}
                      title={
                        [r.brakeBalance, r.brakeNBraking != null ? `${r.brakeNBraking} braking samples` : null]
                          .filter(Boolean)
                          .join(" · ") || undefined
                      }
                    />
                    <td>
                      {chip ? (
                        <span style={st.chip}>{chip}</span>
                      ) : (
                        <span
                          style={{ color: "var(--text-muted)" }}
                          title={pending ? "Not analyzed yet" : "Too few samples to measure"}
                        >
                          —
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div style={st.legend}>
            Understeer: steering degrees per g; higher = more front push. Roll and pitch: body
            motion per g; lower = flatter. Roll and pitch magnitudes assume iRacing's ride-height
            convention; run-to-run comparisons hold even if that scale is off. Brake bal. is the
            front − rear lockup difference; 0 = even, + = front-biased. The scale isn't validated
            in-sim yet — compare runs against each other, don't read it as an absolute %. Stagger:
            wheel-speed side difference through corners. Measured from each run's telemetry, not
            setup advice. Click a row to open that run.
          </div>
        </div>
      )}
    </>
  );
}

function MetricTd({
  value,
  fmt,
  reason,
  title,
  active,
}: {
  value: number | null;
  fmt: (v: number) => string;
  reason: string;
  title?: string;
  active: boolean;
}) {
  if (value == null) {
    return (
      <td style={st.num}>
        <span style={{ color: "var(--text-muted)" }} title={reason}>—</span>
      </td>
    );
  }
  return (
    <td style={{ ...st.num, ...(active ? st.activeCell : null) }} title={title}>
      {fmt(value)}
    </td>
  );
}

function RankSortTh({
  label,
  col,
  sortCol,
  sortDir,
  onSort,
  numeric,
  title,
}: {
  label: string;
  col: RankCol;
  sortCol: RankCol;
  sortDir: RankDir;
  onSort: (col: RankCol) => void;
  numeric?: boolean;
  title?: string;
}) {
  const active = col === sortCol;
  return (
    <th
      aria-sort={active ? (sortDir === "asc" ? "ascending" : "descending") : undefined}
      style={numeric ? { textAlign: "right" } : undefined}
      title={title}
    >
      <button
        type="button"
        style={{ ...st.sortBtn, ...(active ? { color: "var(--text-strong)" } : null) }}
        onClick={() => onSort(col)}
      >
        {label}
        <span style={{ ...st.sortArrow, opacity: active ? 1 : 0.35 }} aria-hidden="true">
          {active ? (sortDir === "asc" ? "↑" : "↓") : "↕"}
        </span>
      </button>
    </th>
  );
}

function BalanceCard({ b, carName }: { b: BalanceResult; carName: string | null }) {
  const grad = (v: number | null) => (v == null || !Number.isFinite(v) ? "—" : `${v.toFixed(1)}°/g`);
  return (
    <div className="card" style={{ ...st.card, marginTop: "1.25rem" }}>
      <div style={st.cardHead}>
        <div style={st.cardTitle}>Balance</div>
        {carName && <span style={st.carTag}>{carName}</span>}
      </div>
      <div style={st.headline}>Understeer gradient {grad(b.understeerGradDegG)}</div>
      <div style={st.caption}>
        Steering degrees per g of lateral load — higher means more front push. Measured, not setup
        advice.{b.lowSample ? " Few cornering samples; read with caution." : ""}
      </div>

      {b.perPhaseGradient && (
        <>
          <div style={st.sectionLabel}>Gradient by corner phase</div>
          <table className="table" style={{ marginBottom: "1rem" }}>
            <thead><tr><th>Phase</th><th style={st.num}>Gradient (°/g)</th></tr></thead>
            <tbody>
              {["entry", "center", "exit"].map((ph) => (
                <tr key={ph}>
                  <td>{PHASE_LABEL[ph] || ph}</td>
                  <td style={st.num}>{grad(b.perPhaseGradient?.[ph] ?? null)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {/* HELD — yaw ratio + push detector pending in-sim validation. */}
      <details style={st.details}>
        <summary style={st.summary}>Yaw &amp; push</summary>
        <div style={st.caption}>
          Not validated against an in-sim reference run yet — read the gradient above.
        </div>
        <div style={st.statGrid}>
          <Stat
            label="Neutral-steer yaw ratio"
            value={b.yawAvailable ? (b.held.yawRatioMedian == null ? "—" : b.held.yawRatioMedian.toFixed(2)) : "rack-steer — n/a"}
            muted
          />
          <Stat label="Push onset" value={b.held.pushPeakSteerDeg == null ? "—" : `${b.held.pushPeakSteerDeg.toFixed(0)}°`} muted />
          <Stat label="Time past push onset" value={b.held.pushPct == null ? "—" : `${b.held.pushPct.toFixed(0)}%`} muted />
        </div>
      </details>
    </div>
  );
}

function StaggerCard({ s, carName }: { s: StaggerResult; carName: string | null }) {
  const headline =
    s.rearSide === "right" ? "Right-side stagger"
    : s.rearSide === "left" ? "Left-side stagger"
    : "Minimal stagger";
  // No path copy when there's no clear side (keeps the two strings from contradicting).
  const pathCopy = s.rearSide === "even" ? "" : (PATH_EFFECT_COPY[s.pathEffect] || "");
  const cap = (v: string) => v.charAt(0).toUpperCase() + v.slice(1);
  const ms = (v: number | null) => (v == null || !Number.isFinite(v) ? "—" : v.toFixed(2));
  const pct = (v: number | null) => (v == null || !Number.isFinite(v) ? "—" : `${v.toFixed(1)}%`);

  return (
    <div className="card" style={{ ...st.card, marginTop: "1.25rem" }}>
      <div style={st.cardHead}>
        <div style={st.cardTitle}>Dynamic stagger</div>
        {carName && <span style={st.carTag}>{carName}</span>}
      </div>
      <div style={st.headline}>{headline}</div>
      <div style={st.caption}>
        Measured wheel-speed side difference — not setup advice.{s.lowSample ? " Few straight-line samples; read with caution." : ""}
        {pathCopy ? ` ${pathCopy}.` : ""}
      </div>

      <div style={st.statGrid}>
        <Stat label="Rear (right − left)" value={cap(s.rearSide)} />
        <Stat label="Front (right − left)" value={cap(s.frontSide)} />
        <Stat label="Straight samples" value={String(s.nStraight)} />
        <Stat label="Corner samples" value={String(s.nCorner)} />
      </div>

      {/* HELD — absolute m/s depends on the unvalidated wheel-speed channel units. */}
      <details style={st.details}>
        <summary style={st.summary}>Raw stagger values</summary>
        <div style={st.caption}>
          Absolute side-difference depends on the iRacing wheel-speed channel units, which aren't
          validated against an in-sim reference run yet — read the direction above, not these numbers.
        </div>
        <div style={st.statGrid}>
          <Stat label="Rear stagger (straight)" value={`${ms(s.held.rearStaggerMs)} m/s · ${pct(s.held.rearStaggerPct)}`} muted />
          <Stat label="Front stagger (straight)" value={`${ms(s.held.frontStaggerMs)} m/s · ${pct(s.held.frontStaggerPct)}`} muted />
          <Stat label="Rear side-diff (corner)" value={`${ms(s.held.rearCornerSideDiffMs)} m/s`} muted />
          <Stat label="Path effect (corner − straight)" value={`${ms(s.held.rearPathEffectMs)} m/s`} muted />
          <Stat label="Mean straight speed" value={`${ms(s.held.meanSpeedMs)} m/s`} muted />
        </div>
      </details>
    </div>
  );
}

function PlatformCard({ p, carName }: { p: PlatformResult; carName: string | null }) {
  const byMode = Object.fromEntries(p.modes.map((m) => [m.mode, m]));
  const pitch = byMode.pitch?.meanMm ?? 0;
  // Sign-driven wording (scale-robust); the measured mm leads, never a fixed mm band.
  const headline =
    pitch < 0 ? `Runs nose-down · pitch ${pitch.toFixed(0)} mm`
    : pitch > 0 ? `Runs nose-up · pitch ${pitch.toFixed(0)} mm`
    : "Flat platform";
  const mm = (v: number) => (Number.isFinite(v) ? `${v.toFixed(0)} mm` : "—");
  const perG = (v: number | null) => (v == null ? "—" : `${v.toFixed(1)} mm/g`);

  return (
    <div className="card" style={{ ...st.card, marginTop: "1.25rem" }}>
      <div style={st.cardHead}>
        <div style={st.cardTitle}>Ride-height platform</div>
        {carName && <span style={st.carTag}>{carName}</span>}
      </div>
      <div style={st.headline}>{headline}</div>
      <div style={st.caption}>Measured ride-height attitude across the run — not setup advice.{p.lowSample ? " Few racing samples; read with caution." : ""}</div>

      <div style={st.statGrid}>
        {["heave", "pitch", "roll", "warp"].map((m) => {
          const s = byMode[m];
          if (!s) return null;
          return (
            <div key={m} style={st.stat}>
              <div style={st.statLabel}>{MODE_LABEL[m]} <span style={{ color: "var(--text-muted)" }}>· {MODE_HINT[m]}</span></div>
              <div style={st.statValue}>{mm(s.meanMm)} <span style={{ fontSize: "0.85rem", fontWeight: 500, color: "var(--text-secondary)" }}>range {mm(s.rangeMm)}</span></div>
            </div>
          );
        })}
      </div>

      <div style={st.statGrid}>
        <Stat label="Roll per g" value={perG(p.rollPerG)} />
        <Stat label="Pitch per g" value={perG(p.pitchPerG)} />
      </div>

      {p.perPhase && (
        <details style={st.details}>
          <summary style={st.summary}>By corner phase</summary>
          <table className="table" style={{ marginTop: "0.5rem" }}>
            <thead><tr><th>Phase</th><th style={st.num}>Samples</th><th style={st.num}>Heave</th><th style={st.num}>Pitch</th><th style={st.num}>Roll</th><th style={st.num}>Warp</th></tr></thead>
            <tbody>
              {Object.entries(p.perPhase).map(([ph, row]) => {
                const thin = row.n < MIN_PHASE_SAMPLES;  // don't print a flat "0 mm" off ~no samples
                const cell = (v: number) => (thin ? "—" : (v ?? 0).toFixed(0));
                return (
                  <tr key={ph} style={thin ? { color: "var(--text-muted)" } : undefined}>
                    <td>{PHASE_LABEL[ph] || ph}</td>
                    <td style={st.num}>{row.n}</td>
                    <td style={st.num}>{cell(row.heave)}</td>
                    <td style={st.num}>{cell(row.pitch)}</td>
                    <td style={st.num}>{cell(row.roll)}</td>
                    <td style={st.num}>{cell(row.warp)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </details>
      )}
    </div>
  );
}

function BrakeCard({ b, carName }: { b: BrakeResult; carName: string | null }) {
  const headline =
    b.balance === "balanced" ? "Front and rear lock together"
    : b.balance === "front locks first" ? "Fronts lock before rears under braking"
    : "Rears lock before fronts under braking";

  const pct = (v: number | null, d = 0) => (v == null ? "—" : `${v.toFixed(d)}%`);

  return (
    <div className="card" style={st.card}>
      <div style={st.cardHead}>
        <div style={st.cardTitle}>Brake balance &amp; lockup</div>
        {carName && <span style={st.carTag}>{carName}</span>}
      </div>

      <div style={st.headline}>{headline}</div>
      <div style={st.caption}>Measurement from this run — not setup advice.{b.lowSample ? " Few braking samples; read with caution." : ""}</div>

      <div style={st.statGrid}>
        <Stat label="Braking samples" value={String(b.nBraking)} />
        <Stat label="ABS active" value={pct(b.absActivePct)} />
        <Stat label="Static front bias" value={pct(b.staticFrontBiasPct, 1)} />
        <Stat label="Dynamic bias" value={b.dynBiasMeanPct == null ? "—" : `${b.dynBiasMeanPct.toFixed(1)}% ±${(b.dynBiasRangePct ?? 0).toFixed(1)}`} />
      </div>
      <div style={st.caption}>Bias is read from the setup/telemetry as percent.</div>

      {/* Per-phase balance — front-vs-rear (symmetric, scale-invariant), gated on samples */}
      {b.perPhase && (
        <>
          <div style={st.sectionLabel}>Locks more, by corner phase</div>
          <table className="table" style={{ marginBottom: "1rem" }}>
            <thead><tr><th>Phase</th><th style={st.num}>Samples</th><th>Result</th></tr></thead>
            <tbody>
              {Object.entries(b.perPhase).map(([ph, row]) => {
                const more = row.n < MIN_PHASE_SAMPLES ? "too few to call"
                  : Math.abs(row.frontSlipPct - row.rearSlipPct) < 1 ? "even"
                  : row.frontSlipPct > row.rearSlipPct ? "front locks more" : "rear locks more";
                return (
                  <tr key={ph}>
                    <td>{PHASE_LABEL[ph] || ph}</td>
                    <td style={st.num}>{row.n}</td>
                    <td style={{ color: row.n < MIN_PHASE_SAMPLES ? "var(--text-muted)" : undefined }}>{more}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}

      {/* HELD — these depend on the in-sim-unvalidated absolute slip scale + the 15%
          lockup threshold, so they sit behind the disclosure, not in the trusted body. */}
      <details style={st.details}>
        <summary style={st.summary}>Raw slip detail</summary>
        <div style={st.caption}>
          These depend on an absolute slip scale and a 15% lockup threshold that aren't validated
          against an in-sim reference run yet — read the front/rear balance above, not these numbers.
        </div>
        <div style={st.statGrid}>
          <Stat label="Front slip (mean)" value={`${b.held.frontSlipPct.toFixed(1)}%`} muted />
          <Stat label="Rear slip (mean)" value={`${b.held.rearSlipPct.toFixed(1)}%`} muted />
        </div>
        <table className="table" style={{ marginTop: "0.5rem" }}>
          <thead><tr><th>Wheel</th><th style={st.num}>Locked</th><th style={st.num}>Events</th></tr></thead>
          <tbody>
            {b.held.wheels.map((w) => (
              <tr key={w.wheel}>
                <td style={st.muted}>{w.wheel}</td>
                <td style={{ ...st.num, color: "var(--text-muted)" }}>{w.lockedPct.toFixed(0)}%</td>
                <td style={{ ...st.num, color: "var(--text-muted)" }}>{b.lockupEvents[w.wheel] ?? 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </div>
  );
}

function Stat({ label, value, muted }: { label: string; value: string; muted?: boolean }) {
  return (
    <div style={st.stat}>
      <div style={st.statLabel}>{label}</div>
      <div style={{ ...st.statValue, color: muted ? "var(--text-muted)" : "var(--text-strong)" }}>{value}</div>
    </div>
  );
}

const st: Record<string, CSSProperties> = {
  selectorRow: { display: "flex", alignItems: "center", gap: 12, marginBottom: "1.25rem" },
  selectorLabel: { fontFamily: "var(--font-body)", fontSize: 13, color: "var(--text-secondary)", fontWeight: 600 },
  select: {
    background: "var(--ink-2)", color: "var(--text-default)", border: "1px solid var(--rule-strong)",
    borderRadius: "var(--r-md)", padding: "0.5rem 0.7rem", fontFamily: "var(--font-body)", fontSize: 13, minWidth: 280,
  },
  card: { padding: "1.25rem 1.5rem", maxWidth: 640 },
  cardHead: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 },
  cardTitle: { fontFamily: "var(--font-display)", fontWeight: 700, fontSize: "1rem", color: "var(--text-strong)" },
  carTag: { fontFamily: "var(--font-body)", fontSize: 12, color: "var(--text-secondary)", border: "1px solid var(--rule)", borderRadius: 999, padding: "0.1rem 0.6rem" },
  headline: { fontFamily: "var(--font-display)", fontWeight: 700, fontSize: "1.35rem", color: "var(--text-strong)", marginTop: "0.85rem" },
  caption: { fontFamily: "var(--font-body)", fontSize: 13, color: "var(--text-secondary)", marginTop: 4, lineHeight: 1.45, maxWidth: 460 },
  statGrid: { display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "0.75rem 1.5rem", margin: "1.1rem 0" },
  stat: { borderTop: "1px solid var(--rule)", paddingTop: "0.5rem" },
  statLabel: { fontFamily: "var(--font-body)", fontSize: 12, color: "var(--text-secondary)" },
  statValue: { fontFamily: "var(--font-body)", fontSize: "1.1rem", fontWeight: 700, fontVariantNumeric: "tabular-nums", marginTop: 2 },
  sectionLabel: { fontFamily: "var(--font-body)", fontSize: 12, fontWeight: 600, color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: "0.04em", margin: "0.4rem 0 0.5rem" },
  num: { textAlign: "right", fontVariantNumeric: "tabular-nums" },
  muted: { fontFamily: "var(--font-body)", fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.5 },
  details: { marginTop: "0.5rem", borderTop: "1px solid var(--rule)", paddingTop: "0.75rem" },
  summary: { fontFamily: "var(--font-body)", fontSize: 13, fontWeight: 600, color: "var(--text-secondary)", cursor: "pointer" },
  // -- Compare runs (#298) --
  viewRow: { marginBottom: "1.1rem" },
  segment: {
    display: "inline-flex", padding: "2px", gap: "2px",
    border: "1px solid var(--rule)", borderRadius: "var(--r-md)",
    background: "var(--ink-1)",
  },
  seg: {
    cursor: "pointer", border: "none", background: "transparent",
    color: "var(--text-secondary)", fontFamily: "var(--font-body)",
    fontSize: "0.8125rem", fontWeight: 600, padding: "0.3rem 0.8rem",
    borderRadius: "var(--r-sm)", lineHeight: 1.2,
  },
  segOn: { background: "var(--live)", color: "var(--ink-0)" },
  tableCard: { padding: "0.5rem 1.5rem 1rem", overflowX: "auto" },
  countLine: { fontFamily: "var(--font-body)", fontSize: 13, color: "var(--text-secondary)", margin: "0.75rem 0 0.25rem" },
  rowClick: { cursor: "pointer" },
  subLine: { fontSize: 13, color: "var(--text-muted)" },
  carCell: { display: "inline-flex", alignItems: "center", gap: 8 },
  chip: {
    fontFamily: "var(--font-body)", fontSize: 13, color: "var(--text-secondary)",
    border: "1px solid var(--rule)", borderRadius: 999, padding: "0.05rem 0.55rem",
    whiteSpace: "nowrap",
  },
  activeCell: { color: "var(--text-strong)", fontWeight: 600 },
  sortBtn: {
    background: "none", border: "none", padding: 0, font: "inherit",
    color: "inherit", letterSpacing: "inherit", textTransform: "inherit",
    cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 5,
  },
  sortArrow: { fontSize: 13, lineHeight: 1 },
  legend: {
    fontFamily: "var(--font-body)", fontSize: 13, color: "var(--text-secondary)",
    lineHeight: 1.45, marginTop: "0.85rem", maxWidth: 640,
  },
};
